import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setHeaderData } from '../slices/headerSlice.mjs';
import { fetchAndAddArticle } from '../slices/siteArticlesSlice.mjs';
import { setPageTitle, setPageDescription, setPageMetadata, setCanonicalUrl, resetPageMetadata } from '../slices/pageMetadataSlice.mjs';
import { buildCanonicalArticleUrl } from '../utils/buildArticleUrl.mjs';
import { resetConversation, selectIsConversationExpanded } from '../slices/conversationSlice.mjs'; // Import selector
import { fetchVersionHistory, resetVersionState } from '../../plugins/external/shared/ai/cms/slices/articleVersionsSlice.mjs';
import useIsMounted from '../hooks/useIsMounted.mjs';
import ArticleContent from '../components/articles/ArticleContent.mjs';
import ConversationView from '../components/conversation/ConversationView.mjs';
import LikeDislikeButtons from '../components/reactions/LikeDislikeButtons.mjs'; // Add reaction buttons

const LARGE_BREAKPOINT_QUERY_AD = '(min-width: 1024px)'; // lg breakpoint for ArticleDetail

const ArticleDetailContent = () => {
    const params = useParams();
    const paramArticleId = params.articleId;
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const { user } = useSelector((state) => state.auth);
    const {
        currentArticle: article,
        currentArticleId,
        isLoadingArticle,
        articleError,
        previousArticle,
    } = useSelector((state) => state.siteArticles);

    const isConversationGloballyExpanded = useSelector(selectIsConversationExpanded); // Get from Redux
    const [isLargeScreenForGrid, setIsLargeScreenForGrid] = useState(false); // Local state for grid class
    const [isSourcesExpanded, setIsSourcesExpanded] = useState(false); // ADD THIS LINE HERE
    const articleId = paramArticleId || currentArticleId;

    // Article versions state
    const {
        versions = [],
        currentVersion = null,
        selectedVersionIndex = -1,
        isLoadingVersions = false,
    } = useSelector((state) => state.articleVersions || {});
    const [summaryHeight, setSummaryHeight] = useState(null);
    const summaryRef = useRef(null);

    // Add the toggle handler function here
    const handleSourcesToggle = () => {
        setIsSourcesExpanded((prev) => !prev);
    };

    // Modify the effect for measuring summary height
    useEffect(() => {
        // Exit early if conditions aren't met
        if (!isLargeScreenForGrid || !article?.summary) return;

        const measureSummary = () => {
            // Make sure the ref element exists before measuring
            if (!summaryRef.current) return;

            // Use getBoundingClientRect for more accurate measurement
            const summaryRect = summaryRef.current.getBoundingClientRect();
            // Add a small buffer (2px) to prevent any rounding issues
            setSummaryHeight(Math.ceil(summaryRect.height) + 2);
        };

        // Safely set up ResizeObserver
        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined') {
            try {
                resizeObserver = new ResizeObserver(() => {
                    // Always check ref is still valid
                    if (summaryRef.current) {
                        requestAnimationFrame(measureSummary);
                    }
                });

                // Critical fix: Only observe if element exists
                if (summaryRef.current) {
                    resizeObserver.observe(summaryRef.current);
                }
            } catch (error) {
                console.error('ResizeObserver error:', error);
            }
        }

        // Still measure on window resize as backup
        window.addEventListener('resize', measureSummary);

        // Initial measurement
        requestAnimationFrame(measureSummary);

        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            window.removeEventListener('resize', measureSummary);
        };
    }, [article?.summary, isLargeScreenForGrid]);

    useEffect(() => {
        // For screen size detection for grid class
        const mediaQueryList = window.matchMedia(LARGE_BREAKPOINT_QUERY_AD);
        const handleResize = () => {
            setIsLargeScreenForGrid(mediaQueryList.matches);
        };
        setIsLargeScreenForGrid(mediaQueryList.matches); // Initial check
        mediaQueryList.addEventListener('change', handleResize);
        return () => mediaQueryList.removeEventListener('change', handleResize);
    }, []);

    useEffect(() => {
        if (articleId) {
            dispatch(resetConversation()); // This will also reset isConversationExpanded if you keep that in resetConversation
            dispatch(resetVersionState()); // Reset version state when switching articles
        }
        if (articleId && (!currentArticleId || currentArticleId !== articleId)) {
            dispatch(fetchAndAddArticle(articleId));
            // Load version history for this article
            dispatch(fetchVersionHistory({ articleId }));
        }
        if (article) {
            const pageTitle = article.title || 'Article Detail';
            const rawDesc = article.summary || 'View detailed article information';
            const pageDesc = rawDesc.length <= 160 ? rawDesc
                : (rawDesc.lastIndexOf(' ', 159) > 0
                    ? rawDesc.substring(0, rawDesc.lastIndexOf(' ', 159))
                    : rawDesc.substring(0, 159)) + '\u2026';
            dispatch(setPageTitle(pageTitle));
            dispatch(setPageDescription(pageDesc));
            const articleImage = article.generatedImages?.[0]?.url || article.images?.[0]?.url || null;
            const baseUrl = typeof window !== 'undefined'
                ? `${window.location.protocol}//${window.location.host}`
                : '';
            const canonicalUrl = buildCanonicalArticleUrl(article, baseUrl);
            dispatch(setCanonicalUrl(canonicalUrl));
            dispatch(setPageMetadata({
                'og:title': pageTitle,
                'og:description': pageDesc,
                'og:type': 'article',
                'og:url': canonicalUrl,
                ...(articleImage ? { 'og:image': articleImage } : {}),
                'twitter:card': articleImage ? 'summary_large_image' : 'summary',
                'twitter:title': pageTitle,
                'twitter:description': pageDesc,
                ...(articleImage ? { 'twitter:image': articleImage } : {}),
            }));
            dispatch(
                setHeaderData({
                    isArticlePage: true,
                    title: 'Nexus of Truth',
                    description: `Written by: ${article.authorPersona?.name || ''}`,
                })
            );
        }
        return () => {
            dispatch(resetPageMetadata());
        };
    }, [articleId, currentArticleId, article, dispatch]);

    const renderLink = (to, className, children) => {
        const combinedClassName = className;
        if (isMounted) {
            return (
                <Link to={to} className={combinedClassName}>
                    {children}
                </Link>
            );
        } else {
            return (
                <a href={to} className={combinedClassName}>
                    {children}
                </a>
            );
        }
    };

    const renderSourceArticle = (sourceArticle, index) => {
        return (
            <div className="article-source-item" key={`source-${index}-${sourceArticle._id}`}>
                <div className="article-source-header">
                    {sourceArticle.category && (
                        <span className="article-source-category">{sourceArticle.category}</span>
                    )}
                    <h4 className="article-source-title">
                        <a href={sourceArticle.url} target="_blank" rel="noopener noreferrer">
                            {sourceArticle.title || 'Untitled Source'}
                        </a>
                    </h4>
                </div>

                {sourceArticle.site && (
                    <div className="article-source-site">
                        <span className="article-source-site-label">Source:</span>
                        <span className="article-source-site-value">{sourceArticle.site}</span>
                    </div>
                )}

                {sourceArticle.generatedArticleSimilarityScore !== undefined && (
                    <div className="article-source-similarity">
                        <span className="article-source-similarity-label">Similarity Score:</span>
                        <span className="article-source-similarity-value">
                            {typeof sourceArticle.generatedArticleSimilarityScore === 'number'
                                ? sourceArticle.generatedArticleSimilarityScore.toFixed(2)
                                : sourceArticle.generatedArticleSimilarityScore}
                        </span>
                    </div>
                )}

                {Array.isArray(sourceArticle.relatedArticles) &&
                    sourceArticle.relatedArticles.length > 0 && (
                        <div className="article-source-related">
                            <h5 className="article-source-related-title">Related Articles:</h5>
                            <ul className="article-source-related-list">
                                {sourceArticle.relatedArticles.map((related, relIdx) => (
                                    <li
                                        key={`related-${relIdx}-${related.url}`}
                                        className="article-source-related-item"
                                    >
                                        <a
                                            href={related.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {related.title || 'Related Article'}
                                        </a>
                                        {related.site && (
                                            <span className="article-source-related-site">
                                                {related.site}
                                            </span>
                                        )}
                                        {related.roundedScore !== undefined && (
                                            <span className="article-source-related-score">
                                                Score:{' '}
                                                {typeof related.roundedScore === 'number'
                                                    ? related.roundedScore.toFixed(2)
                                                    : related.roundedScore}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                {Array.isArray(sourceArticle.significantRelations) &&
                    sourceArticle.significantRelations.length > 0 && (
                        <div className="article-source-significant">
                            <h5 className="article-source-significant-title">
                                Significant Relations:
                            </h5>
                            <ul className="article-source-significant-list">
                                {sourceArticle.significantRelations.map((relation, relIdx) => (
                                    <li
                                        key={`significant-${relIdx}-${relation.articleId || ''}`}
                                        className="article-source-significant-item"
                                    >
                                        <div className="article-source-significant-chunk">
                                            <a
                                                href={relation.articleId}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {relation.chunk || 'Related Content'}
                                            </a>
                                        </div>

                                        {relation.sources &&
                                            relation.sources.length > 0 &&
                                            relation.sources[0].contribution !== undefined && (
                                                <div className="article-source-significant-contribution">
                                                    <span className="article-source-significant-label">
                                                        Contribution:
                                                    </span>
                                                    <span className="article-source-significant-value">
                                                        {typeof relation.sources[0].contribution ===
                                                            'number'
                                                            ? relation.sources[0].contribution.toFixed(
                                                                2
                                                            )
                                                            : relation.sources[0].contribution}
                                                    </span>
                                                </div>
                                            )}

                                        {relation.sources &&
                                            relation.sources.length > 0 &&
                                            relation.sources[0].sourceChunk && (
                                                <div className="article-source-significant-source">
                                                    <span className="article-source-significant-label">
                                                        Source:
                                                    </span>
                                                    <a
                                                        href={relation.sources[0].sourceArticleId}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {relation.sources[0].sourceChunk}
                                                    </a>
                                                </div>
                                            )}

                                        {relation.sourceCounts &&
                                            Object.keys(relation.sourceCounts).length > 0 && (
                                                <div className="article-source-significant-counts">
                                                    <span className="article-source-significant-label">
                                                        Source Counts:
                                                    </span>
                                                    <ul className="article-source-significant-counts-list">
                                                        {Object.entries(relation.sourceCounts).map(
                                                            ([source, count], countIdx) => (
                                                                <li
                                                                    key={`count-${countIdx}-${source}`}
                                                                    className="article-source-significant-counts-item"
                                                                >
                                                                    <span className="article-source-significant-counts-source">
                                                                        {source}:
                                                                    </span>
                                                                    <span className="article-source-significant-counts-value">
                                                                        {count}
                                                                    </span>
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
            </div>
        );
    };

    const renderArticleContent = () => {
        if (articleError) {
            // Handle both string and object error formats
            const errorMessage = typeof articleError === 'string'
                ? articleError
                : articleError?.message || 'An error occurred loading this article';

            return (
                <div className="article-error-message">
                    <h2>Error loading article</h2>
                    <p>{errorMessage}</p>
                    <div className="back-link mt-4">
                        {renderLink('/q/home', 'btn', 'Back to Home')}
                    </div>
                </div>
            );
        }
        if (isLoadingArticle && previousArticle) {
            return (
                <article className="article-content loading-transition">
                    <div className="loading-overlay">
                        <div className="loading-spinner"></div>
                        <p>Loading new article...</p>
                    </div>
                    <h1 className="article-title fade-out">{previousArticle.title}</h1>
                    {previousArticle.summary && (
                        <div className="article-summary fade-out">
                            <p>{previousArticle.summary}</p>
                        </div>
                    )}
                    {previousArticle && (
                        <div className="fade-out">
                            <ArticleContent
                                title={previousArticle.title}
                                body={previousArticle.body}
                                bodyArray={previousArticle.bodyArray}
                                generatedImages={previousArticle.generatedImages || []}
                                articleId={previousArticle._id}
                                showTitle={false}
                                editable={false}
                            />
                        </div>
                    )}
                </article>
            );
        }
        if (isLoadingArticle && !article) {
            return (
                <div className="article-loading-container">
                    <div className="article-loading-spinner"></div>
                    <p>Loading article...</p>
                </div>
            );
        }

        if (article) {
            // Determine the class for the main grid container
            const gridLayoutClass = `article-layout-grid ${isConversationGloballyExpanded && isLargeScreenForGrid
                ? 'conversation-grid-expanded-lg'
                : ''
                } ${isSourcesExpanded ? 'sources-expanded' : ''}`.trim();

            return (
                <>
                    {article.authorPersona && (
                        <div className="article-author-persona">
                            {/* Name + Location */}
                            <div className="persona-main">
                                Author:
                                <h2 className="persona-name"> {article.authorPersona.name}, </h2>
                                <span className="persona-location">
                                    {article.authorPersona.place_of_residence}
                                </span>
                            </div>

                            {/* Top 3 Professional Interests */}
                            {article.authorPersona.interests?.professional_interests?.length >
                                0 && (
                                    <div className="persona-tags">
                                        Professional Interests:
                                        {article.authorPersona.interests.professional_interests
                                            .slice(0, 3)
                                            .map((tag, idx) => (
                                                <span key={idx} className="persona-badge">
                                                    {tag}
                                                </span>
                                            ))}
                                    </div>
                                )}

                            {/* Writing Tone */}
                            {article.authorPersona.writing_style?.tone && (
                                <div className="persona-tone">
                                    Writing Style/Tone:
                                    <span className="persona-badge">
                                        {article.authorPersona.writing_style.tone}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Mobile-only title - appears above summary on mobile */}
                    <div className="article-mobile-title-section lg:hidden">
                        <h1 className="article-title">{article.title}</h1>

                        {/* Add Like/Dislike Buttons for mobile */}
                        <div className="article-reactions-section">
                            <LikeDislikeButtons
                                articleId={articleId}
                                variant="detail"
                                showCounts={true}
                            />
                        </div>
                    </div>
                    <div className={gridLayoutClass}>
                        <div className="article-layout-left">
                            {article.summary &&
                                !(isConversationGloballyExpanded && isLargeScreenForGrid) && (
                                    <div
                                        ref={summaryRef}
                                        className="article-summary-column lg:sticky lg:top-24"
                                    >
                                        <h3 className="article-summary-title">Summary</h3>
                                        <div className="article-summary">
                                            <p>{article.summary}</p>
                                        </div>
                                    </div>
                                )}

                            {user && articleId && (
                                <ConversationView
                                    user={user}
                                    articleId={articleId}
                                    summaryHeight={summaryHeight}
                                    versionContext={{
                                        hasVersions: versions.length > 0,
                                        currentVersionId: currentVersion?._id || null,
                                        selectedVersionIndex,
                                        versionNumber: currentVersion?.versionNumber || null,
                                        isViewingVersion: selectedVersionIndex >= 0,
                                    }}
                                />
                            )}
                            {!user && article && (
                                <div className="article-summary-column mt-6 lg:sticky lg:top-24">
                                    <p className="text-sm text-neutral-600 dark:text-neutral-400 p-4">
                                        {renderLink(
                                            '/login',
                                            'text-blue-600 hover:underline',
                                            'Log in'
                                        )}{' '}
                                        or{' '}
                                        {renderLink(
                                            '/signup',
                                            'text-blue-600 hover:underline',
                                            'sign up'
                                        )}{' '}
                                        to chat with the AI persona about this article.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="article-layout-center">
                            <article className="article-detail-content">
                                {/* Hide title on mobile, show on large screens */}
                                <h1 className="article-title hidden lg:block">{article.title}</h1>

                                {/* Hide Like/Dislike Buttons on mobile (shown in mobile title section), show on large screens */}
                                <div className="article-reactions-section hidden lg:block">
                                    <LikeDislikeButtons
                                        articleId={articleId}
                                        variant="detail"
                                        showCounts={true}
                                    />
                                </div>

                                {article && (
                                    <ArticleContent
                                        title={article.title}
                                        body={article.body}
                                        bodyArray={article.bodyArray}
                                        generatedImages={article.generatedImages || []}
                                        articleId={article._id}
                                        showTitle={false}
                                        editable={false}
                                    />
                                )}
                                <div className="article-navigation">
                                    {renderLink(
                                        '/q/home',
                                        'article-back-to-home',
                                        'Back to Articles'
                                    )}
                                </div>
                            </article>
                        </div>

                        <div className="article-layout-right">
                            {article.sourceArticles &&
                                typeof article.sourceArticles === 'object' && (
                                    <div className="article-sources-section lg:sticky">
                                        <div className="article-sources-header">
                                            <button
                                                onClick={handleSourcesToggle}
                                                className="sources-expand-collapse-button"
                                                aria-label={
                                                    isSourcesExpanded
                                                        ? 'Collapse sources'
                                                        : 'Expand sources'
                                                }
                                            >
                                                <svg
                                                    className="w-5 h-5 transform transition-transform duration-200"
                                                    style={{
                                                        transform: isSourcesExpanded
                                                            ? 'rotate(180deg)'
                                                            : 'rotate(0deg)',
                                                    }}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 9l-7 7-7-7"
                                                    />
                                                </svg>
                                            </button>
                                            <h3 className="article-sources-title">Sources</h3>
                                        </div>
                                        <div className="article-sources-container">
                                            {renderSourceArticle(article.sourceArticles, 0)}
                                        </div>
                                    </div>
                                )}
                        </div>
                    </div>
                </>
            );
        }

        return (
            <div className="not-found">
                <h2>Article not found</h2>
                <p>The requested article could not be found.</p>
                <div className="back-link">{renderLink('/q/home', 'btn', 'Back to Home')}</div>
            </div>
        );
    };

    return (
        <div className="article-detail-container">{renderArticleContent()}</div>
    );
};

const ArticleDetail = (props) => {
    return <ArticleDetailContent />;
};

export default ArticleDetail;
